import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { Autocomplete, InputAdornment, Stack, TextField, Typography, createFilterOptions, styled } from '@mui/material';
import type { HTMLAttributes, SyntheticEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import useProductCatalogActions from '../hooks/useProductCatalogActions';
import useProductCatalogFiltering from '../hooks/useProductCatalogFiltering';
import useProductCatalogSearchOptions, { type ProductSearchOption } from '../hooks/useProductCatalogSearchOptions';

const PRODUCT_SEARCH_DEBOUNCE_DELAY = 400;
const PRODUCT_SEARCH_OPTION_MIN_LENGTH = 3;
const filterProductSearchOptions = createFilterOptions<ProductSearchOption>({
  limit: 12,
  stringify: option => option.searchText,
});

const ProductSearchField = styled(TextField)(({ theme }) => ({
  width: '100%',
}));

interface ProductCatalogSearchControlProps {
  includeCategoryOptions?: boolean
  onCategoryOptionSelect?: (option: ProductSearchOption) => void
  optionFilter?: (option: ProductSearchOption) => boolean
  resetSearchToAllProducts?: boolean
}

export default function ProductCatalogSearchControl({
  includeCategoryOptions = true,
  onCategoryOptionSelect,
  optionFilter,
  resetSearchToAllProducts = true,
}: ProductCatalogSearchControlProps) {
  const [t] = useTranslation('products');
  const navigate = useNavigate();
  const {
    addAttributeFilter,
    getSearchParamsWithoutProductSearch,
    updateProductSearch,
  } = useProductCatalogActions();
  const {
    productSearchQuery,
  } = useProductCatalogFiltering();
  const [productSearchValue, setProductSearchValue] = useState(productSearchQuery);
  const productSearchHasInput = productSearchValue.trim().length >= PRODUCT_SEARCH_OPTION_MIN_LENGTH;
  const allProductSearchOptions = useProductCatalogSearchOptions(includeCategoryOptions);
  const productSearchOptions = useMemo(() => {
    return optionFilter === undefined
      ? allProductSearchOptions
      : allProductSearchOptions.filter(optionFilter);
  }, [allProductSearchOptions, optionFilter])

  const getProductSearchOptionLabel = useCallback((option: ProductSearchOption | string) => {
    return typeof option === 'string' ? option : option.label;
  }, [])

  const renderProductSearchOption = useCallback((optionProps: HTMLAttributes<HTMLLIElement> & { key: string }, option: ProductSearchOption) => {
    const { key, ...otherOptionProps } = optionProps;

    return <li key={key} {...otherOptionProps}>
      <Stack sx={{ py: 0.5 }}>
        <Typography variant="body2">{option.label}</Typography>
        {option.detail ? <Typography variant="caption" color="text.secondary">{option.detail}</Typography> : null }
      </Stack>
    </li>
  }, [])

  const onProductSearchInputChange = useCallback((_event: SyntheticEvent, value: string, reason: string) => {
    if (reason !== 'input' && reason !== 'clear') return;

    setProductSearchValue(value);
  }, [])

  const onProductSearchChange = useCallback((_event: SyntheticEvent, value: ProductSearchOption | string | null) => {
    if (value === null) return;

    if (typeof value === 'string') {
      setProductSearchValue(value);
      updateProductSearch(value, { replace: true, resetToAllProducts: resetSearchToAllProducts });
      return;
    }

    const nextSearchParams = getSearchParamsWithoutProductSearch();
    setProductSearchValue('');

    if (value.type === 'category' && onCategoryOptionSelect !== undefined) {
      updateProductSearch('', { replace: true });
      onCategoryOptionSelect(value);
      return;
    }

    if (value.type === 'filter') {
      if (value.typeId === undefined || value.valueId === undefined) return;

      addAttributeFilter(value.typeId, value.valueId, { clearProductSearch: true });
      return;
    }

    if (value.path !== undefined) {
      const nextSearch = nextSearchParams.toString();
      void navigate(value.path + (nextSearch === '' ? '' : '?' + nextSearch));
    }
  }, [addAttributeFilter, getSearchParamsWithoutProductSearch, navigate, onCategoryOptionSelect, resetSearchToAllProducts, updateProductSearch])

  useEffect(() => {
    setProductSearchValue(productSearchQuery);
  }, [productSearchQuery])

  useEffect(() => {
    if (productSearchValue === productSearchQuery) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      updateProductSearch(productSearchValue, { replace: true, resetToAllProducts: resetSearchToAllProducts });
    }, PRODUCT_SEARCH_DEBOUNCE_DELAY);

    return () => window.clearTimeout(timeout);
  }, [productSearchQuery, productSearchValue, resetSearchToAllProducts, updateProductSearch])

  return <Autocomplete<ProductSearchOption, false, false, true>
    autoHighlight
    clearOnBlur={false}
    filterOptions={filterProductSearchOptions}
    forcePopupIcon={false}
    freeSolo
    fullWidth
    getOptionLabel={getProductSearchOptionLabel}
    groupBy={option => option.group}
    inputValue={productSearchValue}
    options={productSearchHasInput ? productSearchOptions : []}
    renderOption={renderProductSearchOption}
    value={null}
    onChange={onProductSearchChange}
    onInputChange={onProductSearchInputChange}
    sx={{
      flexShrink: 1,
      flexGrow: 1,
      maxWidth: {
        xs: '100%',
        sm: 320,
      },
      minWidth: {
        sm: 220,
      },
      width: {
        xs: '100%',
        sm: 'auto',
      },
    }}
    renderInput={(inputParams) => <ProductSearchField
      {...inputParams}
      aria-label={t('Search products')}
      placeholder={t('Search products')}
      variant="standard"
      inputProps={{
        ...inputParams.inputProps,
        'aria-label': t('Search products'),
      }}
      InputProps={{
        ...inputParams.InputProps,
        startAdornment: <InputAdornment position="start">
          <SearchRoundedIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
        </InputAdornment>,
      }}
    />}
  />;
}
